import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";
import { getAuthToken } from "./authStore";

// Singleton socket with ref-counting.
// Prevents connection floods when many components mount simultaneously.
let socketInstance = null;
let refCount = 0;
let closeTimer = null;

// Global handler registry: tracks ALL handlers from ALL active proxies.
// When socketInstance is replaced (after manual disconnect), handlers are
// re-registered on the new socket so joinChatBox/joinTickets fire correctly.
const globalHandlers = [];

function connectToSocket() {
    // Cancel any pending deferred close
    if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
    }

    // socket.active is true when connected OR auto-reconnecting.
    // Only create a new socket when there is none, or the previous was manually closed.
    if (!socketInstance || !socketInstance.active) {
        socketInstance = openSocket(getBackendUrl(), {
            transports: ["websocket", "polling"],
            query: { token: getAuthToken() },
        });

        // Re-register handlers from still-active proxies on the new socket instance
        // so joinChatBox / joinTickets are emitted when the new socket connects.
        globalHandlers.forEach(({ event, handler }) => {
            socketInstance.on(event, handler);
        });

        socketInstance.on("disconnect", () => {
            if (socketInstance) {
                socketInstance.io.opts.query = { token: getAuthToken() };
            }
        });
    }

    refCount++;

    // Track which listeners this caller registered
    const myHandlers = [];

    const proxy = {
        on(event, handler) {
            myHandlers.push({ event, handler });
            globalHandlers.push({ event, handler });
            socketInstance.on(event, handler);
            return this;
        },
        off(event, handler) {
            const myIdx = myHandlers.findIndex(h => h.event === event && h.handler === handler);
            if (myIdx !== -1) myHandlers.splice(myIdx, 1);
            const gIdx = globalHandlers.findIndex(h => h.event === event && h.handler === handler);
            if (gIdx !== -1) globalHandlers.splice(gIdx, 1);
            if (socketInstance) socketInstance.off(event, handler);
            return this;
        },
        emit(event, ...args) {
            if (socketInstance) socketInstance.emit(event, ...args);
            return this;
        },
        disconnect() {
            // Remove only this caller's listeners from both the socket and global registry
            myHandlers.forEach(({ event, handler }) => {
                const gIdx = globalHandlers.findIndex(h => h.event === event && h.handler === handler);
                if (gIdx !== -1) globalHandlers.splice(gIdx, 1);
                if (socketInstance) socketInstance.off(event, handler);
            });
            myHandlers.length = 0;

            refCount = Math.max(0, refCount - 1);
            if (refCount === 0) {
                // Defer close: if a new component opens the socket during the same
                // navigation, the close is cancelled above.
                closeTimer = setTimeout(() => {
                    closeTimer = null;
                    if (refCount === 0 && socketInstance) {
                        socketInstance.disconnect();
                        socketInstance = null;
                        globalHandlers.length = 0;
                    }
                }, 200);
            }
        },
        get connected() { return socketInstance ? socketInstance.connected : false; },
        get id() { return socketInstance ? socketInstance.id : null; },
        get io() { return socketInstance ? socketInstance.io : null; },
    };

    return proxy;
}

// Called on logout to force-close regardless of ref count
connectToSocket.disconnectAll = () => {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    refCount = 0;
    globalHandlers.length = 0;
    if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
    }
};

export default connectToSocket;
